import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageContainer } from '../components/layout';
import { Card, Spinner } from '../components/ui';
import ClosureTrail from '../components/closure/ClosureTrail';
import { useRegister } from '../hooks/useRegister';
import { useAuth } from '../auth/useAuth';
import { applyScope } from '../lib/scope';
import type { Identity } from '../lib/scope';
import { SCOPE_CONFIG } from '../config/scopeConfig';
import { defaultScopeFor } from '../config/roles';
import styles from './ClosureTrailPage.module.css';

/**
 * ClosureTrailPage (04-08) — "Where did this report end up?"
 *
 * The differentiator over Centro (parity test #4) on the admin surface: pick a
 * lodged incident and follow it through to its consequences (CI item, care-plan
 * revision, …) in one place — the in-dashboard twin of the trail the Portal
 * already renders for Andy/CMs.
 *
 * The incident PICKER is ROLE-SCOPED: incidents load via `useRegister`
 * (04-02) and pass through the single scope engine `applyScope` (04-03) with the
 * role's default Incidents scope + the injected `SCOPE_CONFIG` — so a Care
 * Manager only browses their Portfolio (grouped fallback today, 04-CONTEXT §8),
 * Oversight sees All. The trail is keyed off the incident's COMPOSED `reference`
 * (`INC-<Id>`, ISS-008) — never the calc Reference column.
 *
 * Deep-linkable: `?ref=INC-42` jumps straight to a trail so a tile/table row
 * (04-05/04-07) can land here. Honest empty state against today's empty
 * registers; presentation-only (the trail itself never mutates — authority §2).
 */
export default function ClosureTrailPage() {
  const { user, roles } = useAuth();
  const { data: incidents, isLoading, error } = useRegister('incidents');
  const [searchParams, setSearchParams] = useSearchParams();

  // The selected source ref comes from the URL (`?ref=INC-42`) so the view is
  // deep-linkable and round-trips when the user picks from the list.
  const selectedRef = searchParams.get('ref');

  // Scope identity (04-03). businessUnits EMPTY → Portfolio grouped fallback
  // (widens nothing; no per-CM BU map yet, 04-CONTEXT §8) — same idiom as
  // RegisterTable so the picker scopes identically to every other surface.
  const identity: Identity = useMemo(
    () => ({ upn: user?.email ?? '', businessUnits: [] }),
    [user?.email],
  );

  // The role's default Incidents scope (Oversight → All; CareManager →
  // Portfolio). `null` (role can't view) fails closed at the scope engine.
  const viewToken = defaultScopeFor(roles, 'incidents') ?? 'Own only';

  // Role-scoped, newest-first incident list for the picker.
  const scopedIncidents = useMemo(() => {
    const scoped = applyScope(viewToken, identity, 'incidents', incidents, SCOPE_CONFIG);
    return [...scoped].sort(
      (a, b) => (Date.parse(b.created) || 0) - (Date.parse(a.created) || 0),
    );
  }, [incidents, identity, viewToken]);

  const selectRef = (ref: string | null) => {
    if (ref) {
      setSearchParams({ ref }, { replace: false });
    } else {
      setSearchParams({}, { replace: false });
    }
  };

  return (
    <PageContainer title="Where did this report end up?" maxWidth="full">
      <p className={styles.intro}>
        Pick an incident to follow it through to its consequences — the
        improvements, care-plan revisions and other follow-ups it set in motion,
        each with its current status, in one place.
      </p>

      <div className={styles.layout}>
        {/* ── Role-scoped incident picker ── */}
        <aside className={styles.picker} aria-label="Pick an incident">
          <h2 className={styles.pickerHeading}>Incidents</h2>

          {isLoading ? (
            <div className={styles.pickerLoading} role="status" aria-live="polite">
              <Spinner size="md" />
              <span>Loading incidents…</span>
            </div>
          ) : error ? (
            <p role="alert" className={styles.pickerError}>
              Couldn’t load incidents: {error}
            </p>
          ) : scopedIncidents.length === 0 ? (
            <Card variant="outlined" padding="md" className={styles.pickerEmpty}>
              <p className={styles.pickerEmptyTitle}>No incidents to follow yet</p>
              <p className={styles.pickerEmptyBody}>
                Incidents you can view will appear here as they’re lodged. Then
                you can trace each one to where it ended up.
              </p>
            </Card>
          ) : (
            <ul className={styles.pickerList}>
              {scopedIncidents.map((incident) => (
                <li key={incident.id}>
                  <button
                    type="button"
                    className={`${styles.pickerItem} ${
                      incident.reference === selectedRef
                        ? styles.pickerItemActive
                        : ''
                    }`}
                    aria-current={incident.reference === selectedRef}
                    onClick={() => selectRef(incident.reference)}
                  >
                    <span className={styles.pickerRef}>{incident.reference}</span>
                    <span className={styles.pickerTitle}>
                      {incident.title || '—'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* ── The selected incident's trail ── */}
        <section className={styles.trailPane} aria-label="Closure trail">
          {selectedRef ? (
            <ClosureTrail sourceRef={selectedRef} />
          ) : (
            <Card variant="outlined" padding="lg" className={styles.prompt}>
              <p className={styles.promptTitle}>Pick an incident</p>
              <p className={styles.promptBody}>
                {isLoading || scopedIncidents.length > 0
                  ? 'Choose an incident on the left to see where the report ended up.'
                  : 'Once incidents are lodged, choose one here to trace it through to its consequences.'}
              </p>
            </Card>
          )}
        </section>
      </div>
    </PageContainer>
  );
}
